import type { ContentElementRow } from './contentElements';

export type ParseMarkdownFn = (markdown: string) => string;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Render one newsletter content block to HTML.
 * Supports markdown, html, and legacy element blocks (element_id; prefer html snapshots).
 * Keep in sync with supabase/functions/materialize-newsletter/index.ts.
 */
export function renderNewsletterBlockToHtml(
  block: unknown,
  elementsById: Map<string, Pick<ContentElementRow, 'id' | 'body_html'>>,
  parseMarkdown: ParseMarkdownFn,
): string {
  if (!block || typeof block !== 'object' || Array.isArray(block)) {
    return `<pre>${escapeHtml(JSON.stringify(block, null, 2))}</pre>`;
  }

  const b = block as Record<string, unknown>;
  const type = typeof b.type === 'string' ? b.type.toLowerCase() : 'markdown';

  if (type === 'element') {
    const elementId = typeof b.element_id === 'string' ? b.element_id.trim() : '';
    const el = elementId ? elementsById.get(elementId) : undefined;
    if (el?.body_html?.trim()) return el.body_html;
    if (elementId) {
      return `<p style="color:#b45309;font-family:sans-serif;font-size:14px;">[Missing element: ${escapeHtml(elementId)}]</p>`;
    }
    return '';
  }

  const content = typeof b.content === 'string' ? b.content : '';

  if (type === 'html') {
    return content;
  }

  if (!content) return '';

  try {
    return parseMarkdown(content);
  } catch {
    return `<pre>${escapeHtml(content)}</pre>`;
  }
}

export function renderNewsletterContentToHtml(
  content: unknown,
  elementsById: Map<string, Pick<ContentElementRow, 'id' | 'body_html'>>,
  parseMarkdown: ParseMarkdownFn,
): string {
  if (!Array.isArray(content)) return '';
  return content
    .map((block, idx) => {
      const html = renderNewsletterBlockToHtml(block, elementsById, parseMarkdown).trim();
      if (!html) return '';
      return `<section data-block-index="${idx}">${html}</section>`;
    })
    .filter(Boolean)
    .join('\n');
}

export function blockUsesElementReference(block: unknown): boolean {
  if (!block || typeof block !== 'object' || Array.isArray(block)) return false;
  const b = block as Record<string, unknown>;
  return (typeof b.type === 'string' ? b.type.toLowerCase() : '') === 'element';
}

export function blockElementId(block: unknown): string | null {
  if (!blockUsesElementReference(block)) return null;
  const id = (block as Record<string, unknown>).element_id;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}
